<!-- profile.component.html -->
<form class="profile-container" [formGroup]="profileForm" (ngSubmit)="onSubmit()">
  <!-- Page header -->
  <div class="page-header">
    <div class="page-header-left">
      <h1 class="page-title">My Profile</h1>
      <p class="page-subtitle">Manage your personal and company information</p>
    </div>
    <div class="page-header-right">
      <ng-container *ngIf="!editMode">
        <button type="button" class="btn btn-primary btn-sm" (click)="toggleEditMode()">
          <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/>
          </svg>
          Edit Profile
        </button>
      </ng-container>
      <ng-container *ngIf="editMode">
        <button type="button" class="btn btn-secondary btn-sm" (click)="toggleEditMode()" [disabled]="isLoading">
          Cancel
        </button>
        <button type="submit" class="btn btn-primary btn-sm" [disabled]="isLoading">
          <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
          </svg>
          {{ isLoading ? 'Saving…' : 'Save' }}
        </button>
      </ng-container>
    </div>
  </div>

  <!-- Hero Card -->
  <div class="hero-card">
    <div class="hero-avatar">
      {{ getProfileInitials() }}
      <div class="avatar-status-dot"></div>
    </div>
    <div class="hero-info">
      <h2 class="hero-name">{{ userProfile?.name || '—' }}</h2>
      <p class="hero-role">Customer · {{ getCompanyName() }}</p>
      <div class="hero-meta">
        <div class="meta-item">
          <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
          </svg>
          {{ getEmail() }}
        </div>
        <div class="meta-item">
          <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
          </svg>
          {{ getPhoneNumber() }}
        </div>
        <div class="status-badge">
          <span class="status-dot"></span>
          Online
        </div>
      </div>
    </div>
  </div>

  <!-- Two Column Grid -->
  <div class="profile-grid">
    <!-- Personal Information Card -->
    <div class="info-card">
      <div class="card-header">
        <div class="card-icon">
          <svg viewBox="0 0 20 20" fill="#8b141c" width="16" height="16">
            <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
          </svg>
        </div>
        <h3 class="card-title">Personal Information</h3>
      </div>
      <div class="info-field">
        <div class="field-icon">
          <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
            <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div class="field-body">
          <div class="field-label">Full Name</div>
          <div class="field-value" *ngIf="!editMode">{{ userProfile?.name || '—' }}</div>
          <input *ngIf="editMode" class="field-input" type="text" formControlName="name" placeholder="Full name" />
        </div>
      </div>
      <div class="info-field">
        <div class="field-icon">
          <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
          </svg>
        </div>
        <div class="field-body">
          <div class="field-label">Phone Number</div>
          <div class="field-value" *ngIf="!editMode || !isPhoneEditable()">{{ getPhoneNumber() }}</div>
          <input *ngIf="editMode && isPhoneEditable()" class="field-input" type="tel" formControlName="phoneNumber" placeholder="Phone number" />
        </div>
      </div>
      <div class="info-field">
        <div class="field-icon">
          <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
          </svg>
        </div>
        <div class="field-body">
          <div class="field-label">Email Address</div>
          <div class="field-value" *ngIf="!editMode || !isEmailEditable()">{{ getEmail() }}</div>
          <input *ngIf="editMode && isEmailEditable()" class="field-input" type="email" formControlName="email" placeholder="Email address" />
        </div>
      </div>
      <div class="info-field" *ngIf="editMode">
        <div class="field-icon">
          <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
            <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div class="field-body">
          <div class="field-label">Password (leave blank to keep current)</div>
          <input class="field-input" [type]="showPassword ? 'text' : 'password'" formControlName="password" placeholder="New password (min 8 chars)" />
        </div>
      </div>
    </div>

    <!-- Company Information Card -->
    <div class="info-card">
      <div class="card-header">
        <div class="card-icon">
          <svg viewBox="0 0 20 20" fill="#8b141c" width="16" height="16">
            <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clip-rule="evenodd"/>
          </svg>
        </div>
        <h3 class="card-title">Company Information</h3>
      </div>
      <div class="info-field">
        <div class="field-icon">
          <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
            <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div class="field-body">
          <div class="field-label">Company Name</div>
          <div class="field-value" *ngIf="!editMode">{{ getCompanyName() }}</div>
          <input *ngIf="editMode" class="field-input" type="text" formControlName="companyName" placeholder="Company name" />
        </div>
      </div>
      <div class="info-field">
        <div class="field-icon">
          <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
            <path fill-rule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm2 10a1 1 0 10-2 0 1 1 0 002 0zm2-3a1 1 0 011 1v3a1 1 0 11-2 0v-3a1 1 0 011-1zm2-2a1 1 0 10-2 0 1 1 0 002 0z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div class="field-body">
          <div class="field-label">GST Number</div>
          <div class="field-value" *ngIf="!editMode">{{ getGstNumber() }}</div>
          <input *ngIf="editMode" class="field-input" type="text" formControlName="gstNumber" placeholder="GST number" />
        </div>
      </div>
    </div>
  </div>
</form>
